"use client";

import Link from "next/link";
import { useState } from "react";
import {
  type PasswordSignInCopy,
  PasswordSignInForm,
} from "@/components/sign-in/PasswordSignInForm";
import {
  SIGN_IN_CODE_LENGTH,
  SIGN_IN_LINK,
  type SignInCopy,
  SignInForm,
} from "@/components/sign-in/SignInForm";
import { Alert, AlertDescription } from "@/components/ui/design-system/alert";
import { cn } from "@/lib/utils";

const COPY: SignInCopy = {
  headline: "Sign in",
  lede: `Use your work email. We'll email you a ${SIGN_IN_CODE_LENGTH}-digit code.`,
  checkEmail: "Check your email",
  codeSent: (email) => (
    <>
      We sent a {SIGN_IN_CODE_LENGTH}-digit code to{" "}
      <span className="text-nf-ds-fg">{email}</span>.
    </>
  ),
  emailLabel: "Work email",
  emailPlaceholder: "you@company.com",
  emailSubmit: "Send code",
  google: "Continue with Google",
  codeLabel: "Sign-in code",
  signingIn: "Signing in…",
  resendCountdown: (seconds) => `Resend in ${seconds}s`,
  resend: "Resend code",
  differentEmail: "Use a different email",
  errors: {
    googleFinish: "Google sign-in didn't finish. Try again, or use your email.",
    googleStart: "Couldn't start Google sign-in. Try again.",
    sendFailed: "Couldn't send the code. Try again.",
    codeRejected: "That code didn't work. Check it and try again.",
    verifyFailed: "Couldn't sign you in. Try again.",
    offline: "Couldn't reach NewForm. Check your connection and try again.",
  },
};

/**
 * Self-serve: the brand workspace form, which creates the workspace for
 * whoever is signed in (auth.createOrg).
 */
const CREATE_WORKSPACE_PATH = "/select-organization/brand";

const PASSWORD_COPY: PasswordSignInCopy = {
  headline: "Sign in with a password",
  lede: "Use the email and password you were given.",
  emailLabel: "Email",
  passwordLabel: "Password",
  submit: "Sign in",
  signingIn: "Signing in…",
  errors: {
    rejected: "That email and password don't match. Check them and try again.",
    offline: "Couldn't reach NewForm. Check your connection and try again.",
  },
};

/**
 * Framework's sign-in: the wordmark and the shared form (SignInForm) as one
 * centred block on the app's cream page, as NewForm One's is, without the One
 * rail or the ad wall. Once signed in, /sign-in/continue decides where the
 * person lands, unless a connector sent them (`continueTo` null): Better Auth
 * then answers the sign-in with the next step of the connection, and its
 * client follows it.
 *
 * A password is the exception, for the few logins an engineer creates
 * (PasswordSignInForm), so it sits behind a link rather than beside the code.
 */
export function FrameworkSignIn({
  continueTo,
  googleEnabled,
  noWorkspace,
}: {
  continueTo: string | null;
  googleEnabled: boolean;
  noWorkspace: boolean;
}) {
  const [withPassword, setWithPassword] = useState(false);
  const signedIn = async () => {
    if (continueTo) window.location.assign(continueTo);
  };
  const toggle = (label: string) => (
    <button
      type="button"
      onClick={() => setWithPassword(!withPassword)}
      className={cn(
        SIGN_IN_LINK,
        "self-center px-nf-ds-2 text-nf-ds-fg-muted-on-paper",
      )}
    >
      {label}
    </button>
  );
  return (
    <main className="flex min-h-screen flex-col justify-center px-nf-ds-4 py-nf-ds-16 text-nf-ds-fg">
      <div className="mx-auto flex w-full min-w-0 max-w-md flex-col gap-nf-ds-10">
        <span className="self-center font-swear text-nf-ds-2xl leading-none">
          NewForm
        </span>
        {withPassword ? (
          <PasswordSignInForm
            copy={PASSWORD_COPY}
            idPrefix="sign-in"
            footer={toggle("Use an email code instead")}
            onSignedIn={signedIn}
          />
        ) : (
          <SignInForm
            app="framework"
            idPrefix="sign-in"
            copy={COPY}
            googleEnabled={googleEnabled}
            notice={
              noWorkspace && (
                <Alert variant="warn">
                  <AlertDescription>
                    That email isn't part of a NewForm workspace yet. Ask your
                    workspace's admin to invite you, or sign in with another
                    email. New to NewForm?{" "}
                    <Link
                      href={CREATE_WORKSPACE_PATH}
                      className="font-medium underline underline-offset-2"
                    >
                      Create a workspace
                    </Link>
                  </AlertDescription>
                </Alert>
              )
            }
            footer={toggle("Use a password instead")}
            onPaper
            onSignedIn={signedIn}
          />
        )}
      </div>
    </main>
  );
}
